<template>
  <div class="docs-wrapper">
    <div class="docs-container gap-5">
      <!-- <h2 class="text-2xl font-bold my-4">Documentation</h2> -->
      <div class="endpoints-list w-full">
        <h3 class="text-xl font-semibold documentation">API Documentation</h3>
        <ul class="list-none p-0">
          <li v-for="endpoint in endpoints" :key="endpoint.name" @click="selectEndpoint(endpoint)"
            class="cursor-pointer mb-1 p-2 rounded endpoint">
            {{ endpoint.name }}
          </li>
        </ul>
      </div>
      <div class="endpoint-details w-full" v-if="selectedEndpoint">
        <div class="name-wrapper w-full">
          <h3 class="text-xl font-semibold endpoint-name">{{ selectedEndpoint.name }}</h3>
          <p v-if="selectedEndpoint.method" :class="selectedEndpoint.method.toLowerCase() + ' ' + 'method'">
            <strong>Method:</strong> {{ selectedEndpoint.method }}
          </p>
        </div>
        <div class="description-wrapper">
          <p v-if="selectedEndpoint.url" class="details"><strong>URL:</strong> {{ selectedEndpoint.url }}</p>
          <p v-if="selectedEndpoint.description" class="details"><strong>Description:</strong> {{
            selectedEndpoint.description }}</p>
        </div>
        <div v-if="selectedEndpoint.params?.length > 0">
          <h4 class="text-lg font-semibold mt-4 mb-2 parameters">Parameters</h4>
          <ul class="parameters-wrapper">
            <p class="required">Required</p>
            <li v-for="param in selectedEndpoint.params" :key="param.name" class="mb-1">
              <p class="details"><strong>{{ param.name }}</strong>: {{ param.description }}</p>
            </li>
          </ul>
        </div>
        <div>
          <div v-if="selectedEndpoint.exampleOrder">
            <h4 class="text-lg font-semibold mt-4 mb-2 example">Example Order</h4>
            <pre class="bg-white p-3 rounded example-pre">{{ selectedEndpoint.exampleOrder }}</pre>
          </div>
          <div v-if="selectedEndpoint.exampleRequest">
            <h4 class="text-lg font-semibold mt-4 mb-2 example">Example Request Axios</h4>
            <pre class="bg-white p-3 rounded example-pre">{{ selectedEndpoint.exampleRequest }}</pre>
          </div>
          <div v-if="selectedEndpoint.exampleResponse">
            <h4 class="text-lg font-semibold mt-4 mb-2 example">Example Response</h4>
            <pre class="bg-white p-3 rounded example-pre">{{ selectedEndpoint.exampleResponse }}</pre>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      endpoints: [
        {
          name: 'Getting Started',
          description: 'This API is made for anyone that wants to build on top of the Slant3D Print Factory! If you want to checkout a sample project that utilizes the API follow the link to this repository: https://github.com/slantdev4/branchedPODSample',


        },
        {
          name: 'Authorization',
          description: 'Requesting an API Key is done through the Home Page by clicking the "Request API Key" button. After you have connected a card and your API Key is available, you can use that key to connect to the Slant API to start slicing files and creating orders. Remember to take the proper security measures to ensure that your API key is not lost or used by someone without your authorized access.',
          params: [
          ],

          exampleRequest: `
// This is an example of how to attach your API KEY to a request

let apiKeyValue = "YOUR_PROVIDED_API_KEY";

let res = await api.post('https://www.slant3dapi.com/api/ENDPOINT',
  { 
    headers: { 
    "api-key": apiKeyValue,
    "Content-Type": 'application/json'
    } 
  }
);`
        },
        {
          name: 'Slice File',
          method: 'POST',
          url: 'https://www.slant3dapi.com/api/slicer',
          description: 'Processes a slicing request for a 3D model file. It downloads the file from the provided URL, calculates the slicing metrics, and stores the sliced file and its metrics in the database.',
          params: [
            {
              name: 'fileURL',
              description: 'Attached to the body of the request. This is the URL of the file to be sliced. The file should be accessible from the provided URL for downloading and processing.'
            }
          ],
          exampleResponse: `
{
  "message": "Slicing successful",
  "data": {
    "price": "$8.23"
  }
}`,
          exampleRequest: `
let res = await api.post('https://www.slant3dapi.com/api/slicer',
  { 
    fileURL: 'https://firebasestorage.googleapis.com/v0/b/etsy-tester.appspot.com/o/uploads%2F1-20.stl?alt=media&token=93636d89-1b98-4d61-8249-70a2bac8cd20'
  }, // This fileUrl can be tested with the slicer
  { 
    headers: { 
    "api-key": apiKeyValue,
    "Content-Type": 'application/json'
    }
  }
);`
        },
        {
          name: 'Filament',
          method: 'GET',
          url: 'https://www.slant3dapi.com/api/filament',
          description: `Returns a list of filaments available for ordering via the API`,

          exampleResponse: `
{
    "filaments": [
        {
            "filament": "PLA BLACK", 
            "hexColor": "000000", 
            "colorTag": "black", 
            "profile": "PLA"
        },
        {
            "filament": "PLA WHITE",
            "hexColor": "ffffff",
            "colorTag": "white",
            "profile": "PLA"
        },
        .....
    ]
}
    
hexColor: Representative color of the filament for web display. This does not accurately depict the filament's true color

colorTag: The shortname to use for the color of the filament. This is to be used for API calls that specify a filament such as Create Order

profile:  The profile of the filament. This will be used to support other material types and unique filaments that require a special consideration like PETG. Add this to create order as well as {profile: 'PETG'}`,
          exampleRequest: `
let res = await api.get('https://www.slant3dapi.com/api/filament',

  { 
    headers: { 
    "api-key": apiKeyValue,
    "Content-Type": 'application/json'
    }
  }
);`
        },
        {
          name: 'Create Order',
          method: 'POST',
          url: 'https://www.slant3dapi.com/api/order',
          description: 'Creates an order based on the provided file URL, processes payment via Stripe, where it is put in the order queue at Slant 3D',
          params: [
            {
              name: 'apiKey',
              description: 'API Key attached in the headers of your request'
            },
            {
              name: 'order',
              description: 'This is all of the order data that is used to process an order. Pass an array of orderData objects to assign numerous items to the same order.'
            }
            // Include other parameters as necessary
          ],
          exampleResponse: `
{
  "orderId": "314144241",
}`,
          exampleOrder: `
let orderNumber = 'My_Own_Generated_Order_Number';

let fileData = {
  filename: 'Test Object',
  fileUrl: 'url goes here',
  quantity: '1', // Number as a string
  color: 'Black',
  profile: 'PLA'
};

let customerDetails = {
  name: 'John Doe'
  email: 'sample@sample.com',
  phone: '123-123-1234',
  address: '123 River Street'
  city: 'Sample City',
  state: 'State',
  zip: '11111'
};

const orderData = {
    email: customerDetails.email, // REQUIRED
    phone: customerDetails.phone, // REQUIRED
    name: customerDetails.name, // REQUIRED
    orderNumber: orderNumber, // REQUIRED Your own order number logic 
    filename: filename, // REQUIRED
    fileURL: fileUrl, // REQUIRED The same fileUrl that was passed into the Slicer 
    bill_to_street_1: customerDetails.address, // REQUIRED
    bill_to_street_2: '',
    bill_to_street_3: '',
    bill_to_city: customerDetails.city, // REQUIRED
    bill_to_state: customerDetails.state, // REQUIRED
    bill_to_zip: customerDetails.zip, // REQUIRED
    bill_to_country_as_iso: 'US',
    bill_to_is_US_residential: 'true',
    ship_to_name: customerDetails.name, // REQUIRED
    ship_to_street_1: customerDetails.address, // REQUIRED
    ship_to_street_2: '',
    ship_to_street_3: '',
    ship_to_city: customerDetails.city, // REQUIRED
    ship_to_state: customerDetails.state, // REQUIRED Only when shipping to valid US address
    ship_to_zip: customerDetails.zip, // REQUIRED
    ship_to_country_as_iso: 'US', //REQUIRED  Using Country Code ISO 3166-1 Standards
    ship_to_is_US_residential: 'true', // Adjust if not in US
    order_item_name: fileName, // Your Own File Name Logic
    order_quantity: fileData.quantity // REQUIRED Quantity is sent up as a string
    order_image_url: '', // Your own Image URL
    order_sku: orderSKU, // Your own Order SKU logic
    order_item_color: fileData.color, // REQUIRED Must be set as one of our colors we support
    profile: fileData.profile //OPTIONAL defaults to PLA
};
const order = [orderData, ...] //We can add many orderData's here to wrap many items in the same order. 
`,
          exampleRequest: `
let res = await api.post('https://www.slant3dapi.com/api/order', 
    order, // Attached as the body of the request
  { 
    headers: { 
    "api-key": apiKeyValue,
    "Content-Type": 'application/json'
    }
  }
);`
        },
        {
          name: 'Estimate Order',
          method: 'POST',
          url: 'https://www.slant3dapi.com/api/order/estimate',
          description: 'Creates an order estimate based on the provided file URL. Does not process payments for the file itself or go to Slant\'s production system. Has a validator that will pass back any malformed object errors.',
          params: [
            {
              name: 'apiKey',
              description: 'API Key attached in the headers of your request'
            },
            {
              name: 'order',
              description: 'This is all of the order data that is used to process an order. Pass an array of orderData objects to assign numerous items to the same order.'
            }
          ],
          exampleResponse: `
{
    "totalPrice": 9.31, //USD is the default currency
    "shippingCost": 5.56,
    "printingCost": 3.75
}`,
exampleOrder: `
let orderNumber = 'My_Own_Generated_Order_Number';

let fileData = {
  filename: 'Test Object',
  fileUrl: 'url goes here',
  quantity: '1', // Number as a string
  color: 'Black',
  profile: 'PLA'
};

let customerDetails = {
  name: 'John Doe'
  email: 'sample@sample.com',
  phone: '123-123-1234',
  address: '123 River Street'
  city: 'Sample City',
  state: 'State',
  zip: '11111'
};

const orderData = {
    email: customerDetails.email, // REQUIRED
    phone: customerDetails.phone, // REQUIRED
    name: customerDetails.name, // REQUIRED
    orderNumber: orderNumber, // REQUIRED Your own order number logic 
    filename: filename, // REQUIRED
    fileURL: fileUrl, // REQUIRED The same fileUrl that was passed into the Slicer 
    bill_to_street_1: customerDetails.address, // REQUIRED
    bill_to_street_2: '',
    bill_to_street_3: '',
    bill_to_city: customerDetails.city, // REQUIRED
    bill_to_state: customerDetails.state, // REQUIRED
    bill_to_zip: customerDetails.zip, // REQUIRED
    bill_to_country_as_iso: 'US',
    bill_to_is_US_residential: 'true',
    ship_to_name: customerDetails.name, // REQUIRED
    ship_to_street_1: customerDetails.address, // REQUIRED
    ship_to_street_2: '',
    ship_to_street_3: '',
    ship_to_city: customerDetails.city, // REQUIRED
    ship_to_state: customerDetails.state, // REQUIRED Only when shipping to valid US address
    ship_to_zip: customerDetails.zip, // REQUIRED
    ship_to_country_as_iso: 'US', //REQUIRED  Using Country Code ISO 3166-1 Standards
    ship_to_is_US_residential: 'true', // Adjust if not in US
    order_item_name: fileName, // Your Own File Name Logic
    order_quantity: fileData.quantity // REQUIRED Quantity is sent up as a string
    order_image_url: '', // Your own Image URL
    order_sku: orderSKU, // Your own Order SKU logic
    order_item_color: fileData.color, // REQUIRED Must be set as one of our colors we support
    profile: fileData.profile //OPTIONAL defaults to PLA
};
const order = [orderData, ...] //We can add many orderData's here to wrap many items in the same order. 
`,
          exampleRequest: `
let res = await api.post('https://www.slant3dapi.com/api/order', 
    order, // Attached as the body of the request
  { 
    headers: { 
    "api-key": apiKeyValue,
    "Content-Type": 'application/json'
    }
  }
);`
        },
        {
          name: 'Estimate Shipping',
          method: 'POST',
          url: 'https://www.slant3dapi.com/api/order/estimate',
          description: 'Creates an order shipping estimate based on the provided file URL and shipping address. Does not process payments for the file itself or go to Slant\'s production system. Has a validator that will pass back any malformed object errors.',
          params: [
            {
              name: 'apiKey',
              description: 'API Key attached in the headers of your request'
            },
            {
              name: 'order',
              description: 'This is all of the order data that is used to process an order. Pass an array of orderData objects to assign numerous items to the same order.'
            }
          ],
          exampleResponse: `
{
    "shippingCost": 4.81,
    "currencyCode": "usd"
}`,
exampleOrder: `
let orderNumber = 'My_Own_Generated_Order_Number';

let fileData = {
  filename: 'Test Object',
  fileUrl: 'url goes here',
  quantity: '1', // Number as a string
  color: 'Black',
  profile: 'PLA'
};

let customerDetails = {
  name: 'John Doe'
  email: 'sample@sample.com',
  phone: '123-123-1234',
  address: '123 River Street'
  city: 'Sample City',
  state: 'State',
  zip: '11111'
};

const orderData = {
    email: customerDetails.email, // REQUIRED
    phone: customerDetails.phone, // REQUIRED
    name: customerDetails.name, // REQUIRED
    orderNumber: orderNumber, // REQUIRED Your own order number logic 
    filename: filename, // REQUIRED
    fileURL: fileUrl, // REQUIRED The same fileUrl that was passed into the Slicer 
    bill_to_street_1: customerDetails.address, // REQUIRED
    bill_to_street_2: '',
    bill_to_street_3: '',
    bill_to_city: customerDetails.city, // REQUIRED
    bill_to_state: customerDetails.state, // REQUIRED
    bill_to_zip: customerDetails.zip, // REQUIRED
    bill_to_country_as_iso: 'US',
    bill_to_is_US_residential: 'true',
    ship_to_name: customerDetails.name, // REQUIRED
    ship_to_street_1: customerDetails.address, // REQUIRED
    ship_to_street_2: '',
    ship_to_street_3: '',
    ship_to_city: customerDetails.city, // REQUIRED
    ship_to_state: customerDetails.state, // REQUIRED Only when shipping to valid US address
    ship_to_zip: customerDetails.zip, // REQUIRED
    ship_to_country_as_iso: 'US', //REQUIRED  Using Country Code ISO 3166-1 Standards
    ship_to_is_US_residential: 'true', // Adjust if not in US
    order_item_name: fileName, // Your Own File Name Logic
    order_quantity: fileData.quantity // REQUIRED Quantity is sent up as a string
    order_image_url: '', // Your own Image URL
    order_sku: orderSKU, // Your own Order SKU logic
    order_item_color: fileData.color, // REQUIRED Must be set as one of our colors we support
    profile: fileData.profile //OPTIONAL defaults to PLA
};
const order = [orderData, ...] //We can add many orderData's here to wrap many items in the same order. 
`,
          exampleRequest: `
let res = await api.post('https://www.slant3dapi.com/api/order/estimateShipping', 
    order, // Attached as the body of the request
  { 
    headers: { 
    "api-key": apiKeyValue,
    "Content-Type": 'application/json'
    }
  }
);`
        },
        {
          name: 'Shipping and Tracking',
          method: 'GET',
          url: 'https://www.slant3dapi.com/api/order/${orderId}/get-tracking',
          description: 'Get tracking numbers and shipping status.',
          params: [
            {
              name: 'OrderId',
              description: 'Included as a parameter in the URL. This is the orderId of the order returned in the Create Order.'
            }
          ],
          exampleResponse: `
{
  "status": "awaiting_shipment",
  "trackingNumbers": []
}`,
          exampleRequest: `
let res = await api.get('https://www.slant3dapi.com/api/order/\${orderId}/get-tracking',

  { 
    headers: { 
    "api-key": apiKeyValue,
    "Content-Type": 'application/json'
    }
  }
);`
        },
        {
          name: 'Get Orders',
          method: 'GET',
          url: 'https://www.slant3dapi.com/api/order/',
          description: 'Get tracking numbers of all the orders tied to your account',
          params: [
          ],
          exampleResponse: `
{
    "ordersData": [
        {
            "orderId": 1234567890,
            "orderTimestamp": {
                "_seconds": 1719510986,
                "_nanoseconds": 710000000
            }
        }
    ]
}`,
          exampleRequest: `
let res = await api.get('https://www.slant3dapi.com/api/order/',

  { 
    headers: { 
    "api-key": apiKeyValue,
    "Content-Type": 'application/json'
    }
  }
);`
        },
        {
          name: 'Cancel Order',
          method: 'DELETE',
          url: 'https://www.slant3dapi.com/api/order/${orderId}',
          description: 'Attempts to delete an order and refund. Orders are only cancellable up until a part has printed. If there are issues with this endpoint, please reach out to us in the discord.',
          params: [
            {
              name: 'OrderId',
              description: 'Included as a parameter in the URL. This is the orderId of the order returned in the Create Order.'
            }
          ],
          exampleResponse: `
{
    "status": "Order cancelled"
}`,
          exampleRequest: `
let res = await api.delete('https://www.slant3dapi.com/api/order/\${orderId}',

  { 
    headers: { 
    "api-key": apiKeyValue,
    "Content-Type": 'application/json'
    }
  }
);`
        },
        {
          name: 'Subscribe Webhook',
          method: 'POST',
          url: 'https://www.slant3dapi.com/api/customer/subscribeWebhook',
          description: 'Subscribes a given webhook endpoint URL for order updates. Currently, this will only tell you when the order gets shipped with tracking information. When succesfully set, we will send a test payload to the endpoint. If there are issues with this endpoint, please reach out to us in the discord.',
          params: [
            {
              name: 'endPoint',
              description: 'This will be passed in the request body. It is the endpoint URL you are wishing to subscribe with.'
            }
          ],
          exampleResponse: `
Request Response:
{
    "message": "Endpoint Configured",
    "endPoint": "https://example.com"
}
    
Webhook Example:
{
  "orderId": "1234567890",
  "status": "SHIPPED",
  "trackingNumber": "ABCDEF123456",
  "carrierCode": "usps"
}
`,
          exampleRequest: `
let res = await api.post('https://www.slant3dapi.com/api/customer/webhookSubscribe',
  {
    "endPoint": endPoint
  },
  { 
    headers: { 
    "api-key": apiKeyValue,
    "Content-Type": 'application/json'
    }
  }
);` },

      ],
      selectedEndpoint: null,
    };
  },
  methods: {
    selectEndpoint(endpoint) {
      this.selectedEndpoint = endpoint;
    },
  },
};
</script>

<style scoped>
.docs-wrapper {
  background-color: #f1f1f1;
  min-height: 100dvh;
  padding-left: 10vw;
  padding-right: 10vw;
  font-family: 'Roboto';

}

.endpoint:hover {
  background-color: #7b86f7;
  color: white;
}

.endpoint-details {
  min-width: 50%;
}

.endpoint-name {
  line-height: 1.3;
  font-size: 2rem;
  width: 100%;
}

.description-wrapper {
  background-color: white;
  padding: 1rem;
  border-radius: 8px;
}

.parameters-wrapper {
  background-color: white;
  padding: 1rem;
  border-radius: 8px;
}

.example {
  padding: 1rem;
  background-color: #7b86f7;
  border-radius: 8px;
  color: white;
}

.example-pre {
  background-color: #242424;
  color: white;
  text-wrap: pretty;
  overflow-x: scroll;
}

.required {
  background-color: #eb3434;
  color: white;
  padding-left: 1rem;
  padding-right: 1rem;
  width: fit-content;
  border-radius: 8px;
  margin-bottom: 1rem;
}

.name-wrapper {
  display: flex;
  justify-content: center;
  flex-direction: column;
  margin-bottom: 1rem;
  padding: 1rem;
  background-color: #7b86f7;
  line-height: 1.5;
  border-radius: 8px;
  color: white;
  font-weight: 700;
}

.docs-container {
  display: flex;
  justify-content: start;
  background-color: #f1f1f1;
  padding-top: 1rem;
  padding-bottom: 1rem;
}

@media (max-width: 768px) {
  .docs-container {
    flex-direction: column;
  }
}

.details {
  line-height: 1.5;
}

.method {
  color: white;
  width: fit-content;
  padding: .5rem;

}

.parameters {
  font-size: 1.5rem;
  background-color: #7b86f7;
  color: white;
  padding: 1rem;
  border-radius: 8px;
  width: fit-content;
}

.documentation {
  background-color: #7b86f7;
  color: white;
  padding: 1rem;
  margin-bottom: 1rem;
  border-radius: 8px;
}

.post {
  background-color: rgb(81, 161, 81);
  border-radius: 8px;
}

.get {
  background-color: rgb(207, 130, 58);
  border-radius: 8px;
}
</style>
